import { Button, Popover } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useMemo, useState } from "react";

import DownloadIcon from "@/assets/icons/download.svg?react";
import { IconButton } from "@/components/UI/TableToolbar/IconButton";

import { downloadAdminCsv } from "./adminExportUtils";

type AdminDownloadMenuProps = {
  configuredRows: object[];
  fullRows: object[];
  fileName: string;
  configuredLabel?: string;
  fullLabel?: string;
  configuredTooltip?: string;
  fullTooltip?: string;
  dataTestId?: string;
};

function DownloadMenuItem({
  label,
  tooltipText,
  onClick,
  disabled,
}: {
  label: string;
  tooltipText: string;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex flex-row gap-2 py-2 px-3 items-center justify-start hover:bg-[#ECFAFF] disabled:cursor-not-allowed transition-opacity"
      disabled={disabled}
      title={tooltipText}
    >
      <span>{label}</span>
    </button>
  );
}

export default function AdminDownloadMenu({
  configuredRows,
  fullRows,
  fileName,
  configuredLabel = "Export configured view",
  fullLabel = "Export full view",
  configuredTooltip = "Exports the data shown in the current configured view.",
  fullTooltip = "Exports the full dataset, excluding configured view filters.",
  dataTestId = "admin-download-menu",
}: AdminDownloadMenuProps) {
  const [isOpen, setIsOpen] = useState(false);

  const hasConfiguredRows = configuredRows.length > 0;
  const hasFullRows = fullRows.length > 0;
  const isDisabled = !hasConfiguredRows && !hasFullRows;

  const configuredFileName = useMemo(
    () => `${fileName}_configured_view`,
    [fileName],
  );
  const fullFileName = useMemo(() => `${fileName}_full_view`, [fileName]);

  const handleConfiguredDownload = () => {
    downloadAdminCsv(configuredRows, configuredFileName);
    setIsOpen(false);
  };

  const handleFullDownload = () => {
    downloadAdminCsv(fullRows, fullFileName);
    setIsOpen(false);
  };

  return (
    <Popover
      content={
        <div
          className="flex flex-col items-start justify-start w-full py-2"
          style={{ minWidth: "200px" }}
        >
          <DownloadMenuItem
            label={configuredLabel}
            tooltipText={configuredTooltip}
            onClick={handleConfiguredDownload}
            disabled={!hasConfiguredRows}
          />
          <DownloadMenuItem
            label={fullLabel}
            tooltipText={fullTooltip}
            onClick={handleFullDownload}
            disabled={!hasFullRows}
          />
        </div>
      }
      title={
        <div className="flex items-center justify-between pb-1 border-b">
          <p className="text-sm font-semibold">Downloads</p>
          <Button
            type="text"
            onClick={() => setIsOpen(false)}
            icon={<CloseOutlined className="text-[#00000073]" />}
          />
        </div>
      }
      trigger="click"
      placement="bottomRight"
      arrow={false}
      open={isOpen}
      onOpenChange={setIsOpen}
      autoAdjustOverflow={false}
      align={{ offset: [0, 10] }}
      destroyOnHidden
    >
      <span>
        <IconButton
          icon={<DownloadIcon />}
          title="Export configured view or full dataset as CSV"
          onClick={() => setIsOpen(!isOpen)}
          isOpen={isOpen}
          disabled={isDisabled}
          data-testid={dataTestId}
          style={{ height: "35px", width: "35px" }}
        />
      </span>
    </Popover>
  );
}
